import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, ExternalLink, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Button } from "@/modern/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/modern/components/ui/collapsible";
import { VIEW_EDITOR_FIELD_GRID_CLASS } from "@/modern/components/editors/viewEditor/components/ViewEditorSection";
import { cn } from "@/modern/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import {
  applyMarketingPageFormData,
  marketingPageSchema,
  marketingPageToFormData,
  type MarketingPageFormData,
} from "@/react/schema/marketingPageSchema";
import { useMarketingPagesContext } from "../context/MarketingPagesContext";
import { confirmSlugRepublishIfNeeded } from "../hooks/useSlugRepublishConfirm";
import {
  Field,
  MarketingPageOgImageUploader,
  PANEL_INPUT_CLASS,
  PANEL_TEXTAREA_CLASS,
  PanelSection,
  SettingsToggleRow,
  SurfaceBlock,
} from "./shared/siteEditorUi";
import { isSiteLive } from "../utils";

const FORM_ID = "marketing-page-detail-form";

function pageHref(rawUrl: string) {
  if (!rawUrl) return "";
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) return rawUrl;
  return `https://${rawUrl}`;
}

export function MarketingPageDetailPanel() {
  const { selectedPage, clearSelectedPage, siteRecord, isRepublishing } = useMarketingPagesContext();
  const { project, selectedBoard } = useSwivuBryntumContext();
  const boardSlug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;
  const [isSaving, setIsSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<MarketingPageFormData>({
    resolver: zodResolver(marketingPageSchema),
    defaultValues: selectedPage ? marketingPageToFormData(selectedPage) : undefined,
  });

  const watchedSlug = watch("slug");

  useEffect(() => {
    if (selectedPage) {
      reset(marketingPageToFormData(selectedPage));
      setAdvancedOpen(false);
    }
  }, [selectedPage, reset]);

  const onSave = useCallback(
    async (data: MarketingPageFormData) => {
      if (!selectedPage || !project) return;
      const slugChanged = data.slug !== (selectedPage.slug ?? "");
      const siteLive = isSiteLive(siteRecord);
      const confirmed = await confirmSlugRepublishIfNeeded(siteLive, slugChanged);
      const triggersRepublish = siteLive && slugChanged && data.includeInSite;
      if (!confirmed) return;

      setIsSaving(true);
      try {
        applyMarketingPageFormData(selectedPage, data);
        await project.sync();
        if (triggersRepublish) {
          toast.info("Republishing your site — track progress in alerts if you leave this view.");
        } else {
          toast.success("Page details saved.");
        }
        reset(marketingPageToFormData(selectedPage));
      } catch {
        toast.error("Could not save page details.");
      } finally {
        setIsSaving(false);
      }
    },
    [selectedPage, project, reset, siteRecord],
  );

  const handleOgImageUpdated = useCallback(() => {
    if (selectedPage) {
      reset(marketingPageToFormData(selectedPage));
    }
  }, [selectedPage, reset]);

  if (!selectedPage) return null;

  const href = pageHref(typeof selectedPage.url === "string" ? selectedPage.url : "");
  const title = selectedPage.title?.trim() || "Untitled page";
  const slugHint = watchedSlug?.trim() ? `/${watchedSlug.trim()}` : null;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card">
      <div className="shrink-0 border-b border-border px-5 pt-4 pb-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-normal leading-none tracking-tight text-muted-foreground">
              Page settings
            </p>
            <h3 className="mt-1.5 truncate text-base font-normal leading-snug tracking-tight text-foreground">
              {title}
            </h3>
          </div>
          <div className="flex shrink-0 items-center gap-0.5 rounded-lg border border-border/70 bg-muted/30 p-0.5">
            {href ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:bg-background/80 hover:text-foreground"
                asChild
              >
                <a href={href} target="_blank" rel="noopener noreferrer" title="Open standalone preview">
                  <ExternalLink className="size-3.5" />
                  Preview
                </a>
              </Button>
            ) : null}
            <Button
              type="submit"
              form={FORM_ID}
              variant="default"
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-xs shadow-sm"
              disabled={isSaving || !isDirty}
            >
              {isSaving ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Save className="size-3" />
              )}
              Save
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="size-7 shrink-0 text-muted-foreground hover:bg-background/80 hover:text-foreground"
              onClick={clearSelectedPage}
              aria-label="Close panel"
            >
              <X className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {isRepublishing && (
        <div className="flex shrink-0 items-center gap-2 border-b border-primary/25 bg-primary/10 px-5 py-2 text-xs text-foreground">
          <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
          Republishing…
        </div>
      )}

      <form
        id={FORM_ID}
        onSubmit={handleSubmit(onSave)}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <div className="min-h-0 flex-1 overflow-y-auto bg-background px-4 py-3.5">
          <div className="space-y-5">
            <PanelSection label="Publishing">
              <Controller
                name="includeInSite"
                control={control}
                render={({ field }) => (
                  <SettingsToggleRow
                    id="includeInSite"
                    label="Include in site"
                    description="Shows on your board site when published."
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isSaving}
                  />
                )}
              />
            </PanelSection>

            <PanelSection label="Address" hint="URL path for this page on your site">
              <Field
                label="Slug"
                required
                error={errors.slug?.message}
                hint={
                  slugHint ? (
                    <span className="inline-flex rounded-md bg-muted/50 px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
                      {slugHint}
                    </span>
                  ) : undefined
                }
              >
                <Controller
                  name="slug"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="slug"
                      {...field}
                      disabled={isSaving}
                      className={cn(PANEL_INPUT_CLASS, "font-mono")}
                      autoComplete="off"
                      placeholder="roof-leak-repair"
                    />
                  )}
                />
              </Field>
            </PanelSection>

            <PanelSection label="Search appearance" hint="How search engines describe this page">
              <Field label="Page title">
                <Controller
                  name="metaTitle"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="metaTitle"
                      {...field}
                      disabled={isSaving}
                      className={PANEL_INPUT_CLASS}
                      placeholder="Page title for search results"
                    />
                  )}
                />
              </Field>
              <Field label="Description">
                <Controller
                  name="metaDescription"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      id="metaDescription"
                      {...field}
                      disabled={isSaving}
                      className={PANEL_TEXTAREA_CLASS}
                      placeholder="Short summary shown in search listings"
                    />
                  )}
                />
              </Field>
              <Field label="Canonical URL" hint="Optional. Use if this page lives at a custom URL.">
                <Controller
                  name="canonicalUrl"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="canonicalUrl"
                      {...field}
                      disabled={isSaving}
                      className={PANEL_INPUT_CLASS}
                      placeholder="https://"
                    />
                  )}
                />
              </Field>
            </PanelSection>

            <PanelSection label="When shared on social" hint="Link preview on Facebook, iMessage, etc.">
              <Field label="Share title">
                <Controller
                  name="ogTitle"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="ogTitle"
                      {...field}
                      disabled={isSaving}
                      className={PANEL_INPUT_CLASS}
                      placeholder="Social share title"
                    />
                  )}
                />
              </Field>
              <Field label="Share description">
                <Controller
                  name="ogDescription"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      id="ogDescription"
                      {...field}
                      disabled={isSaving}
                      className={cn(PANEL_TEXTAREA_CLASS, "min-h-[60px]")}
                      placeholder="Social share description"
                    />
                  )}
                />
              </Field>
              <Field label="Share image" hint="1200×630 recommended for link previews">
                <MarketingPageOgImageUploader
                  page={selectedPage}
                  boardSlug={boardSlug}
                  disabled={isSaving}
                  onUpdated={handleOgImageUpdated}
                />
              </Field>
            </PanelSection>

            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Advanced</h4>
                <SurfaceBlock padded={false}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent/50"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">Indexing & Twitter card</p>
                        <p className="text-[11px] text-muted-foreground/80">
                          Crawl directives and social card type
                        </p>
                      </div>
                      <ChevronDown
                        className={cn(
                          "size-4 shrink-0 text-muted-foreground/70 transition-transform",
                          advancedOpen && "rotate-180",
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-4 border-t border-border bg-muted/20 px-4 pb-4 pt-3">
                      <div className="overflow-hidden rounded-md border border-border/80 bg-background">
                        <div className={VIEW_EDITOR_FIELD_GRID_CLASS}>
                          <Controller
                            name="robotsIndex"
                            control={control}
                            render={({ field }) => (
                              <div className="px-3">
                                <SettingsToggleRow
                                  id="robotsIndex"
                                  label="Allow indexing"
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isSaving}
                                />
                              </div>
                            )}
                          />
                          <Controller
                            name="robotsFollow"
                            control={control}
                            render={({ field }) => (
                              <div className="px-3">
                                <SettingsToggleRow
                                  id="robotsFollow"
                                  label="Follow links"
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isSaving}
                                />
                              </div>
                            )}
                          />
                        </div>
                      </div>
                      <Field label="Twitter card type" hint="Usually leave as summary_large_image.">
                        <Controller
                          name="twitterCard"
                          control={control}
                          render={({ field }) => (
                            <Input
                              id="twitterCard"
                              {...field}
                              disabled={isSaving}
                              className={cn(PANEL_INPUT_CLASS, "font-mono")}
                              placeholder="summary_large_image"
                            />
                          )}
                        />
                      </Field>
                    </div>
                  </CollapsibleContent>
                </SurfaceBlock>
              </div>
            </Collapsible>
          </div>
        </div>
      </form>
    </div>
  );
}
